/**
 * Shared payment-surface pieces. The reception boundary lives here: every
 * component takes a PaymentRow (clinical fields already stripped) and never
 * renders notes. Toman is presentation (core/clinic/money.ts).
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import a from '../access/access.module.css';
import k from '@/ui/ui.module.css';
import { Button, Chip, Notice, TextField } from '@/ui/kit';
import { ConfirmDialog } from '@/ui/formkit';
import { parseNumericInput } from '@/core/i18n/digits';
import { fetchPatient } from '@/core/clinic/patientsApi';
import {
  overridePayableAmount,
  PAYMENT_METHODS,
  SessionErrorCode,
  type PaymentMethod,
} from '@/core/clinic/sessionsApi';
import { modeLabelKey } from '../scheduling/bookingRules';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatDate, formatTime } from '@/core/calendar/format';
import { formatMoney, storageUnit } from '@/core/clinic/money';
import { ApiError } from '@/core/api/errors';
import { useClinicSettings } from '../patients/useClinicSettings';
import { useStaffNames } from '../assessments/useStaffNames';
import { staffNameOf, type PaymentRow } from '../sessions/sessionRules';

export function usePaymentFormatters() {
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const currency = settings?.currencyCode ?? null;
  return {
    money: (v: string | number | null) => formatMoney(v, locale, currency),
    date: (iso: string) => formatDate(iso, { locale, calendar: cal, timezone: tz }),
    when: (iso: string) =>
      `${formatDate(iso, { locale, calendar: cal, timezone: tz })} · ${formatTime(iso, { locale, timezone: tz })}`,
  };
}

/**
 * The payment method as one compact pill row (POS card · Online · Cash ·
 * Card-to-card) — a radio group, keyboard-reachable, POS card preselected by
 * the caller. Replaces the four large method cards on every payment surface.
 */
export function PaymentMethodSelect(props: {
  value: PaymentMethod;
  onChange: (m: PaymentMethod) => void;
  label?: string;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  return (
    <div className={k.field}>
      <span className={k.fieldTop}>
        <span className={k.fieldLabel}>{props.label ?? t('py.methodTitle')}</span>
      </span>
      <div
        className={k.segment}
        role="radiogroup"
        aria-label={props.label ?? t('py.methodTitle')}
        style={{ flexWrap: 'wrap' }}
      >
        {PAYMENT_METHODS.map((m) => {
          const on = props.value === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={on}
              className={[k.segmentOpt, on ? k.segmentOptOn : ''].join(' ')}
              onClick={() => props.onChange(m)}
            >
              {t(`py.method.${m}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function usePatientName(patientId: string | null | undefined): string {
  const q = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => fetchPatient(patientId!),
    enabled: !!patientId,
    staleTime: 5 * 60_000,
  });
  return q.data ? `${q.data.firstName} ${q.data.lastName}` : '…';
}

/** paid · partly paid · unpaid — the account's projection, beside effective / voided. */
export function PaymentChips({ s }: { s: Pick<PaymentRow, 'paymentStatus' | 'effective'> }) {
  const intl = useIntl();
  const key =
    s.paymentStatus === 'paid'
      ? 'py.paid'
      : s.paymentStatus === 'partial'
        ? 'py.partial'
        : 'py.unpaid';
  return (
    <>
      <Chip kind={s.paymentStatus === 'paid' ? 'ok' : 'warn'}>
        {intl.formatMessage({ id: key })}
      </Chip>
      <Chip kind={s.effective ? 'info' : 'err'}>
        {intl.formatMessage({ id: s.effective ? 'ss.effective' : 'ss.voided' })}
      </Chip>
    </>
  );
}

/** The session's operational facts — patient, service, practitioner, delivered — nothing clinical. */
export function SessionFacts({ s, title }: { s: PaymentRow; title: string }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { nameOf } = useStaffNames();
  const f = usePaymentFormatters();
  const name = usePatientName(s.patientId);
  const row = (k: string, v: string) => (
    <div className={a.row}>
      <span className={a.rowKey} style={{ minInlineSize: 130 }}>
        {k}
      </span>
      <span className={a.rowValue} style={{ fontSize: 13 }}>
        {v}
      </span>
    </div>
  );
  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div
        style={{
          padding: '14px 18px 6px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{title}</strong>
        <Chip kind={s.effective ? 'info' : 'err'}>
          {t(s.effective ? 'ss.effective' : 'ss.voided')}
        </Chip>
      </div>
      {row(t('py.patient'), name)}
      {row(t('py.service'), `${s.service.name} — ${t(modeLabelKey(s.locationType))}`)}
      {row(t('py.practitioner'), staffNameOf(s.deliveredByName, s.deliveredByUid, nameOf))}
      {row(t('py.delivered'), f.when(s.deliveredAt))}
    </div>
  );
}

/** "{paid} of {total} paid" — shown wherever a partly paid session's amount is. */
export function PaidOfTotal({ s }: { s: Pick<PaymentRow, 'paidAmount' | 'payableAmount'> }) {
  const intl = useIntl();
  const f = usePaymentFormatters();
  return (
    <span style={{ display: 'block', fontSize: 12, color: 'var(--zc-muted)' }}>
      {intl.formatMessage(
        { id: 'py.paidOfTotal' },
        { paid: f.money(s.paidAmount), total: f.money(s.payableAmount) },
      )}
    </span>
  );
}

/**
 * The one money fact of an OVER-PLAN delivery (Product 2026-09-06): it took
 * the catalogue price, and a payment-recording member may set the agreed
 * amount. The backend answers 409 for any other delivery; the card is
 * offered only where it can succeed and shows the ledgered result.
 */
export function OverPlanAmountCard(props: {
  s: Pick<
    PaymentRow,
    'id' | 'payableAmount' | 'paidAmount' | 'priceBasis' | 'overPlanReason' | 'effective'
  >;
  canCorrect: boolean;
  onCorrected: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const f = usePaymentFormatters();
  const settings = useClinicSettings();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<{ from: string; to: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const n = parseNumericInput(amount);
  const valid = n !== null && n >= 0;
  const save = useMutation({
    mutationFn: () =>
      overridePayableAmount(props.s.id, { payableAmount: n!, reason: reason.trim() || undefined }),
    onSuccess: (row) => {
      setOpen(false);
      setResult({ from: props.s.payableAmount, to: row.payableAmount });
      setAmount('');
      setReason('');
      props.onCorrected();
    },
    onError: (err) => {
      setOpen(false);
      setError(
        err instanceof ApiError && err.code === SessionErrorCode.PayableAmountLocked
          ? (t('py.correctAmountLocked') as string)
          : (err instanceof ApiError && err.serverMessage) || (t('common.failedKept') as string),
      );
    },
  });
  const row = (k: string, v: React.ReactNode) => (
    <div className={a.row}>
      <span className={a.rowKey} style={{ minInlineSize: 130 }}>
        {k}
      </span>
      <span className={a.rowValue} style={{ fontSize: 13 }}>
        {v}
      </span>
    </div>
  );
  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div
        style={{
          padding: '14px 18px 6px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('ss.amountTitle')}</strong>
        <Chip kind="warn">{t('py.overPlan')}</Chip>
      </div>
      {row(
        t('py.amount') as string,
        <strong className="zc-tabular">{f.money(props.s.payableAmount)}</strong>,
      )}
      {row(t('ss.priceBasis') as string, t(`py.priceBasis.${props.s.priceBasis}`))}
      {props.s.overPlanReason &&
        row(
          t('py.overPlanReason', { reason: '' }).toString().replace(/:\s*$/, ':'),
          props.s.overPlanReason,
        )}
      {result && (
        <div className={a.row}>
          <Notice kind="ok">
            {t('py.amountCorrected', { from: f.money(result.from), to: f.money(result.to) })}
          </Notice>
        </div>
      )}
      {error && (
        <div className={a.row}>
          <Notice kind="err" role="alert">
            {error}
          </Notice>
        </div>
      )}
      {props.canCorrect && (
        <div className={a.row}>
          <Button variant="quiet" onClick={() => setOpen(true)}>
            {t('py.correctAmount')}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={open}
        title={t('py.correctAmountTitle') as string}
        body={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span>{t('py.correctAmountBody')}</span>
            <TextField
              label={t('py.correctAmountField')}
              value={amount}
              onChange={setAmount}
              machine
              inputMode="decimal"
              prefix={storageUnit(settings?.currencyCode)}
              requirement={{ word: t('signin.required') as string, kind: 'required' }}
              autoFocus
            />
            <TextField
              label={t('py.correctAmountReason')}
              value={reason}
              onChange={setReason}
              requirement={{ word: t('signin.optional') as string, kind: 'optional' }}
            />
          </div>
        }
        confirmLabel={t('py.correctAmountCta') as string}
        confirmDisabled={!valid}
        busy={save.isPending}
        onConfirm={() => valid && save.mutate()}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
